import { create } from 'zustand';

/**
 * Chromium's beforeinstallprompt, caught as early as possible (main.tsx calls
 * `captureInstallPrompt()` before React mounts) and kept for the install gate's button.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

interface InstallState {
  event: BeforeInstallPromptEvent | null;
  /** The browser said the app was installed (appinstalled) or the person accepted the prompt. */
  installed: boolean;
  /** The last answer to the install dialog, so the gate can say "no problem" after a Cancel. */
  outcome: InstallOutcome | null;
  /** Chromium found this app already installed (getInstalledRelatedApps). */
  alreadyInstalled: boolean;
  promptInstall(): Promise<InstallOutcome>;
}

export const useInstallPrompt = create<InstallState>((set, get) => ({
  event: null,
  installed: false,
  outcome: null,
  alreadyInstalled: false,
  promptInstall: async () => {
    const e = get().event;
    if (!e) return 'unavailable';
    try {
      await e.prompt();
      const choice = await e.userChoice;
      // An accepted prompt consumes the event; a dismissed one can be asked again after a while,
      // and Chromium fires a fresh beforeinstallprompt then, which refills `event`.
      set({ event: null, outcome: choice.outcome, installed: choice.outcome === 'accepted' || get().installed });
      return choice.outcome;
    } catch {
      // prompt() throws when it was already used or needs a gesture.
      set({ event: null, outcome: 'unavailable' });
      return 'unavailable';
    }
  },
}));

/**
 * Chromium on Android can say whether this web app is already installed (the manifest lists
 * itself under related_applications). When it is, the gate says "you already have it" instead
 * of walking through the install. Quietly does nothing elsewhere.
 */
export async function detectInstalledApp(
  nav: { getInstalledRelatedApps?: () => Promise<readonly unknown[]> } = navigator as unknown as {
    getInstalledRelatedApps?: () => Promise<readonly unknown[]>;
  },
): Promise<boolean> {
  try {
    const found = (await nav.getInstalledRelatedApps?.()) ?? [];
    const yes = found.length > 0;
    if (yes) useInstallPrompt.setState({ alreadyInstalled: true });
    return yes;
  } catch {
    return false;
  }
}

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    useInstallPrompt.setState({ event: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () =>
    useInstallPrompt.setState({ installed: true, event: null, outcome: 'accepted' }),
  );
}
